import { call, sync } from 'vuex-pathify';
import { getGeodesicArea, prepareDrawnGeometry, writeGeometryToGeoJSON } from '@/assets/js/mapUtils';
import { GeoJSON } from 'ol/format';
import polygonSplitter from 'polygon-splitter';
import { getPointOnLineExtension } from '@/assets/js/mapUtils';
export default {
  computed: {
    isSplittingPolygonButtonActive: sync('edit/isSplittingPolygonButtonActive'),
  },
  data: () => ({
    currentSplitPolygonEditGeometry: null,
    currentSplitPolygonNewGeometries: [],
    currentSplitPolygonRequiredAttributesValues: {},
    isSplittingPolygonButtonLoading: false,
  }),
  methods: {
    addNonTopoFeatureAction: call('layers/addDatasourceFeature'),
    addTopoFeatureAction: call('layers/addTopoDatasourceFeature'),
    editTopoFeatureAction: call('layers/editTopoDatasourceFeature'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    getLayerFeature: call('layers/getLayerFeature'),
    featureSplittingHandler() {
      if (this.isTopo) {
        this.splitTopoFeature({ geomName: 'geometry' });
      } else {
        this.splitNonTopoFeature({ geomName: this.geometryPropertyName });
      }
    },
    async splitTopoFeature({ geomName } = {}) {
      const r = await this.splitFeatureHandler({
        addAction: this.addTopoFeatureAction,
        editAction: this.editTopoFeatureAction,
        geomName,
      });
      this.$root.$emit(
        'refreshMvtSources',
        r.data.data.map(layer => ({ id: layer, group: 'layers' })),
        true
      );
    },
    async splitNonTopoFeature({ geomName } = {}) {
      await this.splitFeatureHandler({
        addAction: this.addNonTopoFeatureAction,
        editAction: this.editNonTopoFeatureAction,
        geomName,
      });
      this.$root.$emit('refreshMvtSource', this.layerId);
    },
    async splitFeatureHandler({ addAction, editAction, geomName } = {}) {
      this.agreeButtonLoading = true;
      try {
        const editPayload = {
          datasource: this.datasource,
          id: this.selectedFeatureId,
          body: {
            [geomName]: this.currentSplitPolygonEditGeometry,
            properties: {},
          },
          params: { layer_id: this.layerId },
        };
        const r = await editAction(editPayload);
        for await (const geometry of this.currentSplitPolygonNewGeometries) {
          try {
            const addPayload = {
              datasource: this.datasource,
              body: {
                ...this.currentSplitPolygonRequiredAttributesValues,
                [geomName]: {
                  ...geometry,
                  crs: {
                    properties: {
                      name: this.$_config.defaultEpsg,
                    },
                    type: 'name',
                  },
                },
              },
              params: { layer_id: this.layerId },
              skipDefaultErrorHandler: true,
            };
            await addAction(addPayload);
          } catch {
            this.turnOffSplittingPolygon();
          }
        }
        return r;
      } finally {
        this.turnOffSplittingPolygon();
        this.$root.$emit('refreshTableData');
        this.$root.$emit('refreshFormData');
        this.agreeButtonLoading = false;
        this.isDialogVisible = false;
      }
    },
    toggleSplitingPolygon() {
      if (this.isSplittingPolygonButtonActive) {
        this.turnOffSplittingPolygon();
      } else {
        this.turnOnSplittingPolygon();
      }
    },
    turnOffSplittingPolygon() {
      this.$root.$emit('deleteSidebarGeometry');
      this.$root.$emit('turnOffEditing', false);
      this.$root.$emit('selection-action', this.tableSelectionType);
      this.isSplittingPolygonButtonActive = false;
    },
    turnOnSplittingPolygon() {
      this.$root.$emit('deactivateAllTools');
      this.$root.$emit('selection-action');
      this.$root.$emit('drawSidebarGeometry', {
        geometryType: 'LineString',
        drawendCallback: this.onSplitPolygonDrawEnd,
      });
      this.$store.set('snackbar/PUSH_MESSAGE!', {
        message: this.$i18n.t('errors.drawLineThatIntersectPolygon'),
      });
      this.isSplittingPolygonButtonActive = true;
    },
    async onSplitPolygonDrawEnd(e) {
      this.isSplittingPolygonButtonLoading = true;
      const feature = await this.getLayerFeature({ layer_id: this.layerId, feature_id: this.selectedFeatureId });
      const featureGeometry = { ...feature.geometry, crs: feature.crs };
      const splitGeometry = prepareDrawnGeometry(e.feature);
      // Workaround for https://github.com/rowanwins/polygon-splitter/issues/12
      const featureStringGeometry = JSON.stringify(featureGeometry);
      const intersectsLineVertices = splitGeometry.coordinates
        .map((coordinate, idx) => {
          if (featureStringGeometry.includes(JSON.stringify(coordinate))) {
            return idx;
          }
          return null;
        })
        .filter(idx => idx || idx === 0);
      splitGeometry.coordinates = splitGeometry.coordinates.map((coordinate, idx) => {
        if (featureStringGeometry.includes(JSON.stringify(coordinate))) {
          const coords = [splitGeometry.coordinates[idx]];
          if (idx === splitGeometry.coordinates.length - 1 || intersectsLineVertices.find(iIdx => iIdx < idx)) {
            coords.push(splitGeometry.coordinates[idx - 1]);
          } else if (idx === 0 || intersectsLineVertices.find(iIdx => iIdx > idx)) {
            coords.push(splitGeometry.coordinates[idx + 1]);
          } else {
            coords.push(splitGeometry.coordinates[idx - 1]);
          }
          const pointOnLineExtension = getPointOnLineExtension(coords[1], coords[0], 0.01);
          return pointOnLineExtension;
        }
        return coordinate;
      });
      // End of workaround
      const splitResult = polygonSplitter(featureGeometry, splitGeometry);
      const splitFeatures = this.multipolygonToMultiplePoligons(splitResult);
      const newGeoms = [];
      let maxAreaGeom = { geom: '', area: 0 };
      splitFeatures.forEach(feature => {
        const olGeometry = new GeoJSON().readGeometry(feature, {
          dataProjection: this.$_config.defaultEpsg,
          featureProjection: this.$_config.defaultEpsg,
        });
        // Transforming to geojson again, to lose precision of geometry, because backend not allow it
        const gjsonGeometry = writeGeometryToGeoJSON(olGeometry);
        const area = getGeodesicArea(olGeometry);
        if (area > maxAreaGeom.area) {
          if (maxAreaGeom.geom) {
            newGeoms.push(JSON.parse(JSON.stringify(maxAreaGeom.geom)));
          }
          maxAreaGeom = { geom: gjsonGeometry, area };
        } else {
          newGeoms.push(JSON.parse(JSON.stringify(gjsonGeometry)));
        }
      });
      this.isSplittingPolygonButtonLoading = false;
      if (!newGeoms?.length) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.lineMustIntersectPolygon'),
        });
        this.turnOffSplittingPolygon();
      } else {
        const requiredAttributesValues = {};
        const requiredAttributesNames = this.currentLayerAttributesMetadata
          .filter(attribute => !attribute.nullable && !attribute.read_only)
          .map(attribute => attribute.name);
        requiredAttributesNames.forEach(attribute => {
          requiredAttributesValues[attribute] = feature.properties[attribute];
        });
        this.openSplitPolygonConfirmationDialog({
          editGeometry: maxAreaGeom.geom,
          newGeometries: newGeoms,
          requiredAttributesValues,
        });
      }
    },
    openSplitPolygonConfirmationDialog({ editGeometry, newGeometries, requiredAttributesValues }) {
      this.currentSplitPolygonEditGeometry = editGeometry;
      this.currentSplitPolygonNewGeometries = newGeometries;
      this.currentSplitPolygonRequiredAttributesValues = requiredAttributesValues;
      this.agreeButtonLoading = false;
      this.openDialog('splitPolygon');
    },
    splitPolygonHandler() {
      this.featureSplittingHandler();
    },
    multipolygonToMultiplePoligons(gjson) {
      const { type } = gjson;
      if (type === 'Polygon' || type === 'MultiPolygon') return [{ ...gjson }];
      else if (type === 'Feature') {
        const {
          geometry: { coordinates },
        } = gjson;
        return coordinates.map(coord => {
          return {
            coordinates: this.geometryType === 'polygon' ? coord : [coord],
            type: this.geometryType === 'polygon' ? 'Polygon' : 'MultiPolygon',
          };
        });
      }
    },
  },
};
